/**
 * `overlayToAddressDecoration` — the ONE reducer from a precedence-RESOLVED
 * {@link ResolvedOverlays} bundle down to the neutral {@link AddressOverlay}
 * decoration the transpose adapters (graph / scene) stamp onto a node.
 *
 * Every surface that paints an address with its catalog identity — the tx graph
 * nodes, the semantic scene, the flow graph, the address panel/header chips —
 * goes through here, so the "which label / logo / colour / entity wins" choice
 * is made in exactly ONE place and can't drift between surfaces. The overlays
 * are already chain-scoped by the fetch (the batch hook resolves per
 * `(chain, address)`), so this is a pure pick over the resolved bundle.
 *
 * Precedence within the bundle: a curated PRIMARY of a kind wins over the merge
 * leader, which wins over the first hit. Brand (logo/colour) prefers the primary
 * entity's flavour, then the leading label, then the leading tag.
 *
 * RISK is the exception to that precedence, deliberately: it takes the WORST
 * severity across every label and tag rather than the leading one. A curated
 * primary exists to decide what an address is *called*, and letting it decide
 * how dangerous the address is would let "Exchange" outrank "Fake_Phishing" on
 * an address carrying both.
 */
import { addressRisk, catalogRisk, type RiskBearingRow } from "@authe/fe-commons/overlays";
import type { AddressOverlay } from "@authe/fe-commons/transpose";
import type {
  ResolvedCatalogHit,
  ResolvedOverlayEntity,
  ResolvedOverlays,
} from "@authe/fe-commons/overlays";

function primaryIdFor(overlays: ResolvedOverlays, kind: string): string | undefined {
  return overlays.primary.find((p) => p.kind === kind)?.catalogId;
}

/** The winning hit for a kind: curated primary → merge-flagged primary → first. */
function leadingHit(
  hits: ResolvedCatalogHit[],
  primaryId: string | undefined,
): ResolvedCatalogHit | undefined {
  if (primaryId) {
    const match = hits.find((h) => h.catalogId === primaryId);
    if (match) return match;
  }
  return hits.find((h) => h.isPrimary) ?? hits[0];
}

function leadingEntity(overlays: ResolvedOverlays): ResolvedOverlayEntity | undefined {
  const primaryId = primaryIdFor(overlays, "entity");
  if (primaryId) {
    const match = overlays.entities.find((e) => e.id === primaryId);
    if (match) return match;
  }
  return overlays.entities[0];
}

export interface OverlayDecorationOptions {
  /**
   * Turn a stored logo key into a renderable URL (the catalog logos are S3 keys,
   * not URLs). Pass `catalog.resolveLogoUrl`; when omitted the raw key is kept
   * (fine for already-absolute URLs, best-effort otherwise).
   */
  resolveLogoUrl?: (key: string) => string | undefined;
  /**
   * The address's stored row, when the batch was asked for one.
   *
   * Carries the half of risk the catalog cannot see: a band an analyst set by
   * hand. Without it a node whose whole risk is somebody's own judgement drew
   * exactly like an unassessed one — the analyst set `severe` on the address and
   * the graph did not change colour, which is the one place colour IS the
   * signal.
   */
  row?: RiskBearingRow;
}

/**
 * Collapse a resolved bundle into the node decoration. Returns `undefined` when
 * the bundle carries nothing to paint, so callers can leave a node bare rather
 * than stamp an empty overlay.
 */
export function overlayToAddressDecoration(
  overlays: ResolvedOverlays | null | undefined,
  opts: OverlayDecorationOptions = {},
): AddressOverlay | undefined {
  if (!overlays) return undefined;
  const leadLabel = leadingHit(overlays.labels, primaryIdFor(overlays, "label"));
  const leadTag = leadingHit(overlays.tags, primaryIdFor(overlays, "tag"));
  const entity = leadingEntity(overlays);

  const tags = Array.from(
    new Set(overlays.tags.map((t) => t.name).filter((n): n is string => Boolean(n))),
  );
  const label = leadLabel?.name ?? entity?.name;
  const color = entity?.color ?? leadLabel?.color ?? leadTag?.color;
  const rawLogo = entity?.logo ?? leadLabel?.logo ?? leadTag?.logo;
  const logo = rawLogo ? (opts.resolveLogoUrl?.(rawLogo) ?? rawLogo) : undefined;
  const primaryEntityId = entity?.id;

  const decoration: AddressOverlay = {};
  if (label) decoration.label = label;
  if (tags.length > 0) decoration.tags = tags;
  if (color) decoration.color = color;
  if (logo) decoration.logo = logo;
  if (primaryEntityId) decoration.primaryEntityId = primaryEntityId;
  // Its NAME too, not only its id — see `AddressOverlay.primaryEntityName`. It
  // is already resolved right here; only the id was being carried, which left
  // every entity band on a graph titled with a hash.
  if (entity?.name) decoration.primaryEntityName = entity.name;
  // Labels AND tags: an accusation arrives as either, depending on which
  // explorer chip the scraper read it from. Then the worst of that and the row's
  // own recorded band — `addressRisk` is the same rule the address page header
  // uses, so a node and that page cannot disagree about one address.
  const risk = addressRisk([opts.row], catalogRisk(overlays));
  if (risk) {
    decoration.riskLevel = risk.level;
    // Both, because `GraphNode` renders both: a badge showing "High" with a
    // blank beside it reads as a number that failed to load rather than one
    // nobody claimed.
    decoration.riskScore = risk.score;
  }

  return Object.keys(decoration).length > 0 ? decoration : undefined;
}
